import { useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { ClaudeAiAsset, KulaFaviconAsset } from '@kula-ai/assets';
import { Modal, Text, SpinnerLoader } from '@kula-ai/components';
import { Atom01 } from '@kula-ai/icons-core';
import { http } from '@/lib/http';
import { useAuth } from '@/hooks';
import { colors } from '@/theme/foundations/colors';

type ConsentDetails = {
  client_name?: string;
  client_verified?: boolean;
  redirect_host?: string;
  scopes?: string[];
  errors?: string[];
};

export const McpConsent = () => {
  const { currUser } = useAuth();
  const authReq =
    new URLSearchParams(window.location.search).get('auth_req') ?? '';
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [loadError, setLoadError] = useState('');
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    // http's interceptor unwraps to the response body (typed as AxiosResponse, hence the cast).
    (async () => {
      try {
        const data = (await http.get(
          `/mcp/consent?auth_req=${encodeURIComponent(authReq)}`
        )) as unknown as ConsentDetails;
        if (data?.errors?.length) setLoadError(data.errors[0]);
        else setDetails(data);
      } catch {
        setLoadError('err_invalid_auth_request');
      }
    })();
  }, [authReq]);

  const approve = async () => {
    setApproving(true);
    try {
      const data = (await http.post('/mcp/consent', {
        auth_req: authReq,
      })) as unknown as { redirect_url?: string; errors?: string[] };
      if (data?.redirect_url) {
        // Keep the button in its loading state while the browser navigates away —
        // resetting here would let the user re-click during the redirect.
        window.location.href = data.redirect_url;
        return;
      }
      setLoadError(data?.errors?.[0] ?? 'err_invalid_auth_request');
    } catch {
      setLoadError('err_invalid_auth_request');
    }
    setApproving(false);
  };

  // Deny: send the user back to their workspace. The client sees no code and treats it as declined.
  const deny = () => {
    window.location.href = '/dashboard';
  };

  // Title reads "Allow {Client} app to access Kula"; body subject is the client name (sentence-start).
  const clientName = details?.client_name;
  const clientLabel = clientName ? `${clientName} app` : 'this app';
  const clientSubject = clientName || 'This app';
  const userName = [currUser?.first_name, currUser?.last_name]
    .filter(Boolean)
    .join(' ');
  const accountName = currUser?.account?.display_name;
  // Keyed on the redirect host, not client_name: DCR client_name is unauthenticated, so any client
  // could claim "Claude", but the code only ever reaches the registered redirect host.
  const isClaude = /(^|\.)claude\.(ai|com)$/.test(details?.redirect_host ?? '');

  const ready = !loadError && !!details;

  return (
    <Modal
      open
      onOpenChange={() => {}}
      showCloseButton={false}
      size="md"
      className="max-w-[540px]"
      {...(ready
        ? {
            primaryButton: {
              label: 'Allow Access',
              onClick: approve,
              isLoading: approving,
              disabled: approving,
            },
            secondaryButton: {
              label: 'Deny',
              onClick: deny,
              disabled: approving,
            },
          }
        : { footer: null })}
    >
      {loadError ? (
        <>
          <Text variant="h5" color="heading">
            This authorization link is invalid or has expired.
          </Text>
          <Text variant="p" color="muted" className="mt-1">
            Start again from your MCP client.
          </Text>
        </>
      ) : !details ? (
        <Flex justify="center" py="16px">
          <SpinnerLoader />
        </Flex>
      ) : (
        <>
          <Flex align="center" gap="12px" mb="16px">
            {isClaude ? (
              <ClaudeAiAsset
                width={44}
                height={44}
                data-test-id="mcp-claude-icon"
              />
            ) : (
              <Flex
                w="44px"
                h="44px"
                borderRadius="10px"
                flexShrink={0}
                align="center"
                justify="center"
                bg="base-black"
                data-test-id="mcp-ai-icon"
              >
                <Atom01 size={24} color={colors['base-white']} />
              </Flex>
            )}
            <Box
              as="svg"
              width="26px"
              height="26px"
              viewBox="0 0 24 24"
              fill="none"
              flexShrink={0}
              color="grey-700"
            >
              <path
                d="M7 8h11m0 0-3-3m3 3-3 3M17 16H6m0 0 3-3m-3 3 3 3"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Box>
            <KulaFaviconAsset width={44} height={44} />
          </Flex>
          <Text variant="h4" color="heading">
            Allow {clientLabel} to access Kula
          </Text>
          {(userName || accountName) && (
            <Text variant="small" color="muted" className="mt-1">
              Signed in as {userName}
              {accountName ? ` · ${accountName}` : ''}
            </Text>
          )}
          <Text variant="p" color="body" className="mt-4">
            {clientSubject} will be able to view and take actions in Kula on
            your behalf, such as looking up candidates and jobs, updating
            applications, and scheduling interviews.
          </Text>
          <Text variant="p" color="body" className="mt-3">
            It will have the same access as you. Your Kula role and permissions
            decide what it can see and do, so it can&apos;t reach anything you
            can&apos;t.
          </Text>
        </>
      )}
    </Modal>
  );
};
